import {
  forwardRef,
  useId,
  useMemo,
  useRef,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react';
import { Link, type LinkProps } from 'react-router';
import { Check, ChevronDown, CircleAlert, LoaderCircle, X, type LucideIcon } from 'lucide-react';
import { useFormat } from '../format';
import { decimalSeparator, parseAmount, type ParsedAmount } from '../../i18n/parseAmount';

const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(' ');

/* ---------------------------------------------------------------- buttons */

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'md' | 'lg';

interface ButtonLook {
  variant?: ButtonVariant;
  size?: ButtonSize;
  block?: boolean;
  icon?: LucideIcon;
}

export function buttonClass({ variant = 'secondary', size = 'md', block }: ButtonLook, extra?: string) {
  return cx('btn', `btn-${variant}`, `btn-${size}`, block && 'btn-block', extra);
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, ButtonLook {
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant, size, block, icon: Icon, loading, disabled, className, children, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={buttonClass({ variant, size, block }, className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? (
        <LoaderCircle aria-hidden className="spin" size={18} />
      ) : (
        Icon && <Icon aria-hidden size={18} strokeWidth={2} />
      )}
      <span>{children}</span>
    </button>
  );
});

export function ButtonLink({ variant, size, block, icon: Icon, className, children, ...rest }: LinkProps & ButtonLook) {
  return (
    <Link className={buttonClass({ variant, size, block }, className)} {...rest}>
      {Icon && <Icon aria-hidden size={18} strokeWidth={2} />}
      <span>{children as ReactNode}</span>
    </Link>
  );
}

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: LucideIcon;
  /** Required: icon-only controls must name their action. */
  label: string;
  variant?: 'ghost' | 'filled';
}

export function IconButton({ icon: Icon, label, variant = 'ghost', className, type = 'button', ...rest }: IconButtonProps) {
  return (
    <button type={type} className={cx('icon-btn', `icon-btn-${variant}`, className)} aria-label={label} title={label} {...rest}>
      <Icon aria-hidden size={22} strokeWidth={1.9} />
    </button>
  );
}

/* ---------------------------------------------------------------- choice list */

export interface ChoiceOption<T extends string> {
  value: T;
  label: string;
  meta?: ReactNode;
  leading?: ReactNode;
  description?: string;
}

/**
 * A single-choice list (radio group) with large rows: countries,
 * currencies, languages, budget type. Picking a row calls onChange; flows
 * use that to move on immediately. Arrow keys move and select.
 */
export function ChoiceList<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: readonly ChoiceOption<T>[];
  value: T | null;
  onChange: (value: T) => void;
}) {
  const values = options.map((o) => o.value);
  const focusValue = value !== null && values.includes(value) ? value : values[0];
  const { onKeyDown, register } = useRovingKeys(values, focusValue as T, onChange);
  return (
    <div className="choices" role="radiogroup" aria-label={label} onKeyDown={onKeyDown}>
      {options.map((o) => {
        const checked = o.value === value;
        return (
          <button
            key={o.value}
            ref={register(o.value)}
            type="button"
            role="radio"
            aria-checked={checked}
            tabIndex={o.value === focusValue ? 0 : -1}
            className="choice"
            onClick={() => onChange(o.value)}
          >
            {o.leading && <span className="choice-leading">{o.leading}</span>}
            <span className="choice-text">
              <span className="choice-label">{o.label}</span>
              {o.description && <span className="choice-description">{o.description}</span>}
            </span>
            {o.meta !== undefined && <span className="choice-meta">{o.meta}</span>}
            <span className="choice-mark" aria-hidden>
              {checked && <Check size={18} strokeWidth={2.5} />}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * Pick one color from a fixed palette (category line colors). Each swatch
 * names its color and who already uses it, so shared colors are a choice,
 * not an accident.
 */
export function SwatchPicker<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: readonly { value: T; color: string; label: string; note?: string }[];
  value: T;
  onChange: (value: T) => void;
}) {
  const values = options.map((o) => o.value);
  const { onKeyDown, register } = useRovingKeys(values, value, onChange);
  const current = options.find((o) => o.value === value);
  return (
    <div className="field">
      <span className="field-label">{label}</span>
      <div className="swatch-picker" role="radiogroup" aria-label={label} onKeyDown={onKeyDown}>
        {options.map((o) => (
          <button
            key={o.value}
            ref={register(o.value)}
            type="button"
            role="radio"
            aria-checked={o.value === value}
            aria-label={o.note ? `${o.label}, ${o.note}` : o.label}
            tabIndex={o.value === value ? 0 : -1}
            className="swatch-option"
            style={{ ['--swatch' as string]: o.color }}
            onClick={() => onChange(o.value)}
          >
            {o.value === value && <Check aria-hidden size={16} strokeWidth={3} />}
          </button>
        ))}
      </div>
      {current?.note && <p className="field-msg">{current.note}</p>}
    </div>
  );
}

/** Where you are in a short flow: "2 / 6" plus a segmented bar. */
export function StepProgress({ step, total, label }: { step: number; total: number; label: string }) {
  return (
    <div className="step-progress" role="progressbar" aria-label={label} aria-valuemin={1} aria-valuemax={total} aria-valuenow={step}>
      <span className="step-progress-bar" aria-hidden>
        {Array.from({ length: total }, (_, i) => (
          <span key={i} className={i < step ? 'is-done' : undefined} />
        ))}
      </span>
      <span className="step-progress-count num" aria-hidden>
        {step} / {total}
      </span>
    </div>
  );
}

/* ---------------------------------------------------------------- stat tiles */

/**
 * A figure you can press to filter by it (Fixed / Variable / Obligations).
 * Pressed state is a toggle; the figure is tabular so tiles line up.
 */
export function StatTile({
  label,
  value,
  footnote,
  pressed,
  onPress,
  accent,
}: {
  label: string;
  value: ReactNode;
  footnote?: ReactNode;
  pressed?: boolean;
  onPress?: () => void;
  /** Optional strip of the colors inside this figure. */
  accent?: ReactNode;
}) {
  const body = (
    <>
      {/* Always four rows (subgrid): empty slots keep neighbouring figures aligned. */}
      <span className="stat-label">{label}</span>
      <span className="stat-value num">{value}</span>
      <span className="stat-footnote">{footnote}</span>
      <span className="stat-accent" aria-hidden>
        {accent}
      </span>
    </>
  );
  return onPress ? (
    <button type="button" className="stat" aria-pressed={pressed} onClick={onPress}>
      {body}
    </button>
  ) : (
    <div className="stat">{body}</div>
  );
}

/** On/off setting. The whole row is the target; role=switch announces state. */
export function Switch({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  const id = useId();
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-labelledby={`${id}-l`}
      aria-describedby={description ? `${id}-d` : undefined}
      className="switch-row"
      onClick={() => onChange(!checked)}
    >
      <span className="switch-text">
        <span id={`${id}-l`} className="switch-label">
          {label}
        </span>
        {description && (
          <span id={`${id}-d`} className="switch-description">
            {description}
          </span>
        )}
      </span>
      <span className="switch-track" aria-hidden>
        <span className="switch-thumb" />
      </span>
    </button>
  );
}

/* ---------------------------------------------------------------- tiles */

/** A large tap target in a grid of choices, e.g. Quick Add categories. */
export function Tile({
  leading,
  label,
  hint,
  onClick,
  selected,
  autoFocus,
}: {
  leading?: ReactNode;
  label: string;
  hint?: ReactNode;
  onClick: () => void;
  selected?: boolean;
  /** Where the keyboard starts when the sheet opens. */
  autoFocus?: boolean;
}) {
  return (
    <button type="button" className={cx('tile', selected && 'is-selected')} onClick={onClick} aria-pressed={selected} data-autofocus={autoFocus || undefined}>
      {leading && <span className="tile-leading">{leading}</span>}
      <span className="tile-text">
        <span className="tile-label">{label}</span>
        {hint && <span className="tile-hint num">{hint}</span>}
      </span>
    </button>
  );
}

/** Arrow keys move between tiles (left/right along, up/down by one row), like any grid. */
function moveInGrid(e: KeyboardEvent<HTMLDivElement>) {
  const step = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: 'row', ArrowUp: '-row' }[e.key];
  if (!step) return;
  const tiles = [...e.currentTarget.querySelectorAll<HTMLButtonElement>(':scope > .tile')];
  const at = tiles.indexOf(document.activeElement as HTMLButtonElement);
  if (at < 0) return;
  const columns = getComputedStyle(e.currentTarget).gridTemplateColumns.split(' ').filter(Boolean).length || 1;
  const by = step === 'row' ? columns : step === '-row' ? -columns : (step as number);
  const next = tiles[at + by];
  if (!next) return;
  e.preventDefault();
  next.focus();
}

export function TileGrid({ children, label }: { children: ReactNode; label?: string }) {
  return (
    <div className="tile-grid" role="group" aria-label={label} onKeyDown={moveInGrid}>
      {children}
    </div>
  );
}

/* ---------------------------------------------------------------- fields */

interface FieldChrome {
  label: string;
  hint?: ReactNode;
  error?: string;
  /** Hide the label visually (still announced). */
  hideLabel?: boolean;
}

function FieldFrame({
  id,
  label,
  hint,
  error,
  hideLabel,
  children,
}: FieldChrome & { id: string; children: ReactNode }) {
  return (
    <div className={cx('field', error && 'field-invalid')}>
      <label htmlFor={id} className={cx('field-label', hideLabel && 'visually-hidden')}>
        {label}
      </label>
      {children}
      {error ? (
        <p id={`${id}-msg`} className="field-msg field-error">
          <CircleAlert aria-hidden size={16} />
          {error}
        </p>
      ) : (
        hint && (
          <p id={`${id}-msg`} className="field-msg">
            {hint}
          </p>
        )
      )}
    </div>
  );
}

export type TextFieldProps = FieldChrome & Omit<InputHTMLAttributes<HTMLInputElement>, 'id'>;

export function TextField({ label, hint, error, hideLabel, className, ...rest }: TextFieldProps) {
  const id = useId();
  return (
    <FieldFrame id={id} label={label} hint={hint} error={error} hideLabel={hideLabel}>
      <input
        id={id}
        className={cx('input', className)}
        aria-invalid={error ? true : undefined}
        aria-describedby={error || hint ? `${id}-msg` : undefined}
        {...rest}
      />
    </FieldFrame>
  );
}

export type SelectFieldProps = FieldChrome & Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'> & { children: ReactNode };

export function SelectField({ label, hint, error, hideLabel, className, children, ...rest }: SelectFieldProps) {
  const id = useId();
  return (
    <FieldFrame id={id} label={label} hint={hint} error={error} hideLabel={hideLabel}>
      <span className="select-wrap">
        <select
          id={id}
          className={cx('input', 'select', className)}
          aria-invalid={error ? true : undefined}
          aria-describedby={error || hint ? `${id}-msg` : undefined}
          {...rest}
        >
          {children}
        </select>
        <ChevronDown aria-hidden size={18} className="select-chevron" />
      </span>
    </FieldFrame>
  );
}

/* ---------------------------------------------------------------- amount input */

export interface AmountInputProps {
  label: string;
  value: string;
  onValueChange: (text: string, parsed: ParsedAmount) => void;
  currency?: string;
  hint?: ReactNode;
  error?: string;
  autoFocus?: boolean;
  onSubmit?: () => void;
  /** Lets a flow focus the field inside the tap that opened it (mobile keyboards only open on a user gesture). */
  inputRef?: React.Ref<HTMLInputElement>;
  /** A second amount under the main one (a value in another currency): same field, a step smaller. */
  secondary?: boolean;
}

/**
 * The one question the app asks: how much? Big, tabular, the currency symbol
 * placed where the billing region puts it, and a decimal keypad on phones.
 * Accepts either separator (see parseAmount); the parent gets minor units.
 */
export function AmountInput({ label, value, onValueChange, currency: cur, hint, error, autoFocus, onSubmit, inputRef, secondary }: AmountInputProps) {
  const id = useId();
  const { formatLocale, currency: defaultCurrency } = useFormat();
  const currency = cur ?? defaultCurrency;
  const { symbol, symbolAfter, placeholder } = useMemo(() => {
    const parts = new Intl.NumberFormat(formatLocale, { style: 'currency', currency }).formatToParts(0);
    const symbolIndex = parts.findIndex((p) => p.type === 'currency');
    const intIndex = parts.findIndex((p) => p.type === 'integer');
    const sep = decimalSeparator(formatLocale);
    const digits = new Intl.NumberFormat('en', { style: 'currency', currency }).resolvedOptions().maximumFractionDigits ?? 2;
    return {
      symbol: parts[symbolIndex]?.value ?? currency,
      symbolAfter: symbolIndex > intIndex,
      placeholder: digits > 0 ? `0${sep}${'0'.repeat(digits)}` : '0',
    };
  }, [formatLocale, currency]);
  const width = `${Math.max(value.length, placeholder.length) + 0.4}ch`;

  return (
    <div className={cx('amount-field', secondary && 'amount-field-secondary', error && 'field-invalid')}>
      <label htmlFor={id} className="field-label">
        {label}
      </label>
      <div className="amount-field-row">
        {!symbolAfter && <span className="amount-field-symbol" aria-hidden>{symbol}</span>}
        <input
          ref={inputRef}
          id={id}
          className="amount-field-input"
          style={{ width }}
          inputMode="decimal"
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint="done"
          placeholder={placeholder}
          value={value}
          autoFocus={autoFocus}
          aria-invalid={error ? true : undefined}
          aria-describedby={error || hint ? `${id}-msg` : undefined}
          onChange={(e) => onValueChange(e.target.value, parseAmount(e.target.value, currency))}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onSubmit?.();
          }}
        />
        {symbolAfter && <span className="amount-field-symbol" aria-hidden>{symbol}</span>}
      </div>
      {error ? (
        <p id={`${id}-msg`} className="field-msg field-error">
          <CircleAlert aria-hidden size={16} />
          {error}
        </p>
      ) : (
        hint && (
          <p id={`${id}-msg`} className="field-msg">
            {hint}
          </p>
        )
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- segmented control & tabs */

export interface Choice<T extends string> {
  value: T;
  label: string;
}

/** Arrow/Home/End navigation shared by radio groups and tab lists. */
function useRovingKeys<T extends string>(values: readonly T[], current: T, select: (v: T) => void) {
  const refs = useRef(new Map<T, HTMLButtonElement>());
  const onKeyDown = (e: KeyboardEvent) => {
    const i = values.indexOf(current);
    let next = i;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (i + 1) % values.length;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (i - 1 + values.length) % values.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = values.length - 1;
    else return;
    e.preventDefault();
    const v = values[next]!;
    select(v);
    refs.current.get(v)?.focus();
  };
  const register = (v: T) => (el: HTMLButtonElement | null) => {
    if (el) refs.current.set(v, el);
    else refs.current.delete(v);
  };
  return { onKeyDown, register };
}

export function SegmentedControl<T extends string>({
  label,
  options,
  value,
  onChange,
  size = 'md',
}: {
  label: string;
  options: readonly Choice<T>[];
  value: T;
  onChange: (value: T) => void;
  size?: 'sm' | 'md';
}) {
  const values = options.map((o) => o.value);
  const { onKeyDown, register } = useRovingKeys(values, value, onChange);
  const index = Math.max(0, values.indexOf(value));
  return (
    <div
      className={cx('segmented', `segmented-${size}`)}
      role="radiogroup"
      aria-label={label}
      onKeyDown={onKeyDown}
      style={{ ['--n' as string]: options.length, ['--i' as string]: index }}
    >
      <span className="segmented-thumb" aria-hidden />
      {options.map((o) => (
        <button
          key={o.value}
          ref={register(o.value)}
          type="button"
          role="radio"
          aria-checked={o.value === value}
          tabIndex={o.value === value ? 0 : -1}
          className="segmented-option"
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Tabs<T extends string>({
  label,
  items,
  value,
  onChange,
  idPrefix,
}: {
  label: string;
  items: readonly Choice<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Links each tab to its <TabPanel idPrefix=... value=...>. */
  idPrefix: string;
}) {
  const values = items.map((o) => o.value);
  const { onKeyDown, register } = useRovingKeys(values, value, onChange);
  return (
    <div className="tabs" role="tablist" aria-label={label} onKeyDown={onKeyDown}>
      {items.map((o) => (
        <button
          key={o.value}
          ref={register(o.value)}
          id={`${idPrefix}-tab-${o.value}`}
          type="button"
          role="tab"
          aria-selected={o.value === value}
          aria-controls={`${idPrefix}-panel-${o.value}`}
          tabIndex={o.value === value ? 0 : -1}
          className="tab-item"
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function TabPanel({ idPrefix, value, active, children }: { idPrefix: string; value: string; active: boolean; children: ReactNode }) {
  return (
    <div
      id={`${idPrefix}-panel-${value}`}
      role="tabpanel"
      aria-labelledby={`${idPrefix}-tab-${value}`}
      hidden={!active}
      tabIndex={0}
      className="tab-panel"
    >
      {active && children}
    </div>
  );
}

/* ---------------------------------------------------------------- filter chips */

export interface FilterChip {
  key: string;
  text: string;
  /** Removes this filter; without it the chip only says what is shown (e.g. the budget). */
  onRemove?: () => void;
}

/** The filters in effect, under a screen's toolbar: tap one to remove it. */
export function FilterChips({ chips, label, removeLabel }: { chips: FilterChip[]; label: string; removeLabel: (text: string) => string }) {
  if (chips.length === 0) return null;
  return (
    <ul className="filter-chips" aria-label={label}>
      {chips.map((c) => (
        <li key={c.key}>
          {c.onRemove ? (
            <button type="button" className="filter-chip" onClick={c.onRemove} aria-label={removeLabel(c.text)}>
              {c.text}
              <X size={14} aria-hidden />
            </button>
          ) : (
            <span className="filter-chip is-static">{c.text}</span>
          )}
        </li>
      ))}
    </ul>
  );
}
